<template>
  <div>
    <h1 class="page-header">{{ $t('product.product') }}</h1>
    <ts-card>
      <ts-card-wrapper>
        <div class="row">
          <div class="col-sm-4">
            <div class="ts-flex ts-space-x-2">
              <ts-button
                color="info"
                class="mr-2"
                outline
                @click="$router.push({ name: 'product-create' })"
              >
                {{ $t('add_new') }}
              </ts-button>
              <ts-button @click="showForm">
                {{ $t('product.copyPrice') }}
              </ts-button>
              <ts-button @click.prevent="showFormExport">
                {{ $t('product.exportProductPrice') }}
              </ts-button>
            </div>
          </div>
          <div class="col-sm-8 d-flex justify-content-end">
            <ts-input
              v-model="search"
              :placeholder="$t('Search')"
              style="width: 300px"
            />
          </div>
        </div>
      </ts-card-wrapper>
      <div class="overflow-auto">
        <Table
          :columns="columns"
          :data="resources"
          size="small"
          :loading="loading"
          max-height="700"
          highlight-row
          stripe
        >
          <template slot-scope="{ row }" slot="uomCategory">
            {{
              row.u_o_m_category ? row.u_o_m_category.uom_cat_name_en : null
            }}</template
          >
          <template slot-scope="{ row }" slot="category">
            {{ row.category ? row.category.category_name_en : null }}</template
          >
          <template slot-scope="{ row }" slot="supplier">
            {{ row.supplier ? row.supplier.supplier_name_en : null }}</template
          >
          <template slot-scope="{ row }" slot="expirationAvailable">
            <span v-if="row.is_expired">
              <i class="far fa-check-square text-primary"></i>
            </span>
            <span v-else><i class="far fa-window-close text-danger"></i></span>
          </template>
          <template slot-scope="{ row }" slot="calculateStock">
            <span v-if="row.is_calculate_stock">
              <i class="far fa-check-square text-primary"></i
            ></span>
            <span v-else><i class="far fa-window-close text-danger"></i></span>
          </template>
          <template slot-scope="{ row }" slot="hasSalePrice">
            <span class="badge badge-success" v-if="row.has_sale_price"
              >Yes</span
            >
            <span class="badge badge-danger" v-else>No</span>
          </template>
          <template slot-scope="{ row }" slot="inactive">
            <a-switch
              :checked="row.inactive"
              size="small"
              :loading="row.inactive"
              @change="setToInactive(row)"
            />
          </template>
          <template slot-scope="{ row }" slot="action">
            <a
              href="#"
              v-tooltip="'Edit'"
              class="mr-2 text-primary"
              @click.prevent="edit(row)"
            >
              <i class="fas fa-edit"></i>
            </a>
            <a
              href="#"
              v-tooltip="'Delete'"
              class="text-danger"
              @click.prevent="destroy(row)"
              :disabled="row._deleting"
            >
              <i class="fas fa-circle-notch spin" v-if="row._deleting"></i>
              <i class="fas fa-trash" v-else></i>
            </a>
          </template>
        </Table>
      </div>
      <div class="d-flex justify-content-end p-15">
        <ts-pagination v-model="pagination" @navigate="fetchData">
        </ts-pagination>
      </div>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import expandRow from './table-expand.vue'
import CopyPriceForm from './copy-price-from.vue'
import ExportProductPrice from './export-product-price.vue'

export default {
  name: 'product',
  components: { expandRow },
  data() {
    return {
      loading: false,
      category_id: null,
      has_sale_price: null
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    ...mapState('product', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.product.search
      },
      set(newValue) {
        this.$store.commit('product/SET_SEARCH', newValue)
        this.$store.commit('product/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        {
          type: 'expand',
          width: 50,
          render: (h, params) => {
            return h(expandRow, {
              props: {
                row: params.row
              }
            })
          }
        },
        {
          title: this.$t('product.barcode'),
          key: 'barcode',
          align: 'center',
          width: 110
        },
        {
          title: this.$t('product.nameKH'),
          key: 'product_name_kh',
          width: 150
        },
        {
          title: this.$t('product.nameEN'),
          key: 'product_name_en',
          width: 200
        },
        {
          title: this.$t('product.category'),
          slot: 'category',
          width: 200,
          filters: [
            {
              label: 'Study Book',
              value: 1
            },
            {
              label: 'Uniform',
              value: 2
            },
            {
              label: 'Study Material',
              value: 3
            },
            {
              label: 'School Shoes',
              value: 4
            },
            {
              label: 'School Bag',
              value: 5
            },
            {
              label: 'Beverage',
              value: 6
            },
            {
              label: 'Other Material',
              value: 7
            }
          ],
          filterMultiple: true,
          filterRemote(value) {
            this.category_id = value
          }
        },
        {
          title: this.$t('product.uomCategory'),
          slot: 'uomCategory',
          minWidth: 200
        },
        {
          title: this.$t('product.supplier'),
          slot: 'supplier',
          align: 'center',
          minWidth: 110
        },
        {
          title: this.$t('product.expirationAvailable'),
          slot: 'expirationAvailable',
          align: 'center',
          minWidth: 200
        },
        {
          title: this.$t('product.calculateStock'),
          slot: 'calculateStock',
          align: 'center',
          minWidth: 200
        },
        {
          title: this.$t('product.hasSalePrice'),
          slot: 'hasSalePrice',
          align: 'center',
          minWidth: 150,
          filters: [
            {
              label: 'Yes',
              value: true
            },
            {
              label: 'No',
              value: false
            }
          ],
          filterMultiple: false,
          filterRemote(value) {
            this.has_sale_price = value
          }
        },
        {
          title: this.$t('product.inactive'),
          slot: 'inactive',
          align: 'center',
          minWidth: 110
        },
        {
          title: this.$t('actions'),
          slot: 'action',
          fixed: 'right',
          align: 'center',
          width: 90
        }
      ]
    }
  },
  methods: {
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('product/fetch', {
          filter: {
            category_id: this.category_id,
            has_price_set: this.has_sale_price
          },
          ...attributes
        })
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    edit(record) {
      this.$store.commit('product/SET_EDIT_DATA', record)
      this.$router.push({
        name: 'product-edit',
        params: { id: record.product_id }
      })
    },
    async destroy(record) {
      if (await this.$confirmModal.danger('Are you sure to delete ?')) {
        record._deleting = true
        this.$store
          .dispatch('product/destroy', record.product_id)
          .then(response => {
            this.fetchData()
            this.$toasted.success(response.message)
            record._deleting = false
          })
          .catch(error => {
            this.$toasted.error(error.message)
            record._deleting = false
          })
      }
    },
    showForm() {
      this.$modal.show(
        CopyPriceForm,
        {
          data: []
        },
        {
          height: 'auto',
          width: '300px',
          scrollable: true,
          draggable: '.form-header',
          clickToClose: false
        },
        {
          'before-close': this.fetchData
        }
      )
    },
    showFormExport() {
      this.$modal.show(
        ExportProductPrice,
        {
          data: []
        },
        {
          height: 'auto',
          width: '300px',
          scrollable: true,
          draggable: '.form-header',
          clickToClose: false
        }
      )
    },
    async setToInactive(record) {
      if (await this.$confirmModal.danger('Are you sure to inactive ?')) {
        record.inactive = true
        record._deleting = true
        this.$store
          .dispatch('product/setInactive', record.product_id)
          .then(response => {
            this.fetchData()
            this.$toasted.success(response.message)
            record._deleting = false
          })
          .catch(error => {
            this.$toasted.error(error.message)
            record._deleting = false
          })
      }
    }
  },
  watch: {
    search: debounce(function() {
      this.fetchData()
    }, 500),
    category_id: debounce(function() {
      this.fetchData()
    }, 500),
    has_sale_price: debounce(function() {
      this.fetchData()
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    if (_.isEmpty(to.params) && to.name != 'product-create') {
      this.$store.commit('product/RESET_STATE')
    }
    next()
  }
}
</script>
